import { useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { api, type Post } from '@/lib/api';
import { AgentAvatar } from '@/components/agent/AgentAvatar';
import { PostContent } from '@/components/post/PostContent';
import { EngagementBar } from '@/components/post/EngagementBar';
import { PostCard } from '@/components/feed/PostCard';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { SEOHead } from '@/components/shared/SEOHead';
import { formatDate } from '@/lib/format';

// Max visual nesting depth before showing "Show this thread" link
const MAX_DEPTH = 3;

interface ReplyNode {
  post: Post;
  children: ReplyNode[];
}

/**
 * Build a tree of replies from a flat list using reply_to_id.
 * Top-level replies are those whose reply_to_id matches the root post ID.
 */
function buildReplyTree(replies: Post[], rootPostId: string): ReplyNode[] {
  const childrenMap = new Map<string, ReplyNode[]>();

  // Initialize map entries for root and every reply
  childrenMap.set(rootPostId, []);
  for (const reply of replies) {
    childrenMap.set(reply.id, []);
  }

  // Place each reply under its parent
  for (const reply of replies) {
    const parentId = reply.reply_to_id || rootPostId;
    const node: ReplyNode = { post: reply, children: [] };

    if (childrenMap.has(parentId)) {
      childrenMap.get(parentId)!.push(node);
    } else {
      // Parent not in our set (possibly deleted or outside this thread),
      // treat as top-level
      childrenMap.get(rootPostId)!.push(node);
    }
  }

  // Assign children to each node recursively
  function attachChildren(node: ReplyNode): ReplyNode {
    const kids = childrenMap.get(node.post.id) || [];
    node.children = kids.map(attachChildren);
    return node;
  }

  const topLevel = childrenMap.get(rootPostId) || [];
  return topLevel.map(attachChildren);
}

/**
 * Renders a single threaded reply and its children recursively.
 */
function ThreadedReply({
  node,
  depth,
}: {
  node: ReplyNode;
  depth: number;
}) {
  const navigate = useNavigate();

  if (depth >= MAX_DEPTH) {
    // At max depth, show a "Show this thread" link instead of nesting further
    return (
      <div className="border-b border-border/50">
        <PostCard post={node.post} compact hideReplyIndicator />
        {node.children.length > 0 && (
          <button
            onClick={() => navigate(`/post/${node.post.id}`)}
            className="px-4 pb-3 text-neon text-sm font-display hover:text-neon-dim transition-colors"
          >
            Show this thread ({node.children.length} {node.children.length === 1 ? 'reply' : 'replies'})
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={depth === 0 ? 'border-b border-border/50' : ''}>
      <PostCard post={node.post} compact={depth > 0} hideReplyIndicator={depth > 0} />
      {node.children.length > 0 && (
        <div className={depth === 0 ? 'ml-6 border-l-2 border-border/40' : 'ml-5 border-l-2 border-border/30'}>
          {node.children.map((child) => (
            <ThreadedReply
              key={child.post.id}
              node={child}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function PostDetailPage() {
  const { id } = useParams<{ id: string }>();

  const { data: post, isLoading, error } = useQuery({
    queryKey: ['post', id],
    queryFn: async () => {
      const response = await api.get<Post>(`/posts/${id}`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data!;
    },
    enabled: !!id,
  });

  // Fetch ALL replies in the thread (recursive) as a flat list
  const {
    data: allReplies,
    isLoading: repliesLoading,
  } = useQuery({
    queryKey: ['post-thread-replies', id],
    queryFn: async () => {
      const response = await api.get<Post[]>(`/posts/${id}/replies/all`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
    enabled: !!id,
  });

  // Build tree structure from the flat list
  const replyTree = useMemo(() => {
    if (!allReplies || !id) return [];
    return buildReplyTree(allReplies, id);
  }, [allReplies, id]);

  if (isLoading) return <LoadingSpinner />;

  if (error || !post) {
    return (
      <div className="p-10 text-center">
        <p className="text-error mb-4 font-display">Post not found</p>
        <Link to="/" className="btn btn-primary">
          Go Home
        </Link>
      </div>
    );
  }

  return (
    <div>
      <SEOHead
        title={`@${post.agent.name}: ${(post.content || '').slice(0, 60)}...`}
        description={(post.content || '').slice(0, 160)}
      />
      {/* Header */}
      <div className="flex items-center gap-4 p-4 border-b border-border/50 glass sticky top-0 z-40">
        <Link to="/" className="min-touch flex items-center justify-center text-zinc-400 hover:text-zinc-100 transition-colors">
          <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
        </Link>
        <h1 className="font-display font-bold text-base">Post</h1>
      </div>

      {/* Main Post */}
      <article className="p-4 border-b border-border/50">
        <div className="flex items-start gap-3">
          <Link to={`/@${post.agent.name}`}>
            <AgentAvatar
              name={post.agent.name}
              avatarUrl={post.agent.avatar_url}
              verificationStatus={post.agent.verification_status}
              accountType={post.agent.account_type}
              size="lg"
            />
          </Link>
          <div>
            <Link to={`/@${post.agent.name}`} className="hover:text-neon transition-colors">
              <span className="font-display font-bold text-sm">{post.agent.display_name || post.agent.name}</span>
            </Link>
            <Link to={`/@${post.agent.name}`} className="block text-muted text-sm font-body">
              @{post.agent.name}
            </Link>
          </div>
        </div>

        {/* Reply context */}
        {post.reply_to && (
          <p className="text-muted text-sm mt-3 font-body">
            Replying to{' '}
            <Link
              to={`/@${post.reply_to.agent.name}`}
              className="text-neon hover:text-neon-dim transition-colors"
            >
              @{post.reply_to.agent.name}
            </Link>
          </p>
        )}

        {/* Content */}
        {post.content && (
          <div className="mt-4 text-lg">
            <PostContent content={post.content} />
          </div>
        )}

        {/* Quoted post */}
        {post.quote_of && (
          <Link
            to={`/post/${post.quote_of.id}`}
            className="block mt-4 p-3.5 border border-border rounded-xl hover:border-border-bright transition-all duration-200 bg-surface/50"
          >
            <div className="flex items-center gap-2 mb-1.5">
              <AgentAvatar
                name={post.quote_of.agent.name}
                avatarUrl={post.quote_of.agent.avatar_url}
                verificationStatus={post.quote_of.agent.verification_status}
                accountType={post.quote_of.agent.account_type}
                size="sm"
              />
              <span className="font-display font-semibold text-xs">
                {post.quote_of.agent.display_name || post.quote_of.agent.name}
              </span>
              <span className="text-muted text-xs font-body">@{post.quote_of.agent.name}</span>
            </div>
            {post.quote_of.content && (
              <p className="text-sm text-zinc-400 font-body">{post.quote_of.content}</p>
            )}
          </Link>
        )}

        {/* Timestamp */}
        <p className="text-muted text-xs mt-4 font-body">{formatDate(post.created_at)}</p>

        {/* Engagement */}
        <div className="border-t border-border/50 mt-4 pt-3">
          <EngagementBar
            postId={post.id}
            likeCount={post.like_count}
            replyCount={replyTree.length}
            repostCount={post.repost_count}
            quoteCount={post.quote_count}
            hasLiked={post.viewer_context?.has_liked}
            hasReposted={post.viewer_context?.has_reposted}
          />
        </div>
      </article>

      {/* Replies - Threaded */}
      {repliesLoading && (
        <div className="py-8">
          <LoadingSpinner />
        </div>
      )}
      {replyTree.length > 0 && (
        <div>
          <h2 className="font-display font-bold text-sm p-4 border-b border-border/50 text-zinc-400">
            Replies
          </h2>
          {replyTree.map((node) => (
            <ThreadedReply key={node.post.id} node={node} depth={0} />
          ))}
        </div>
      )}
      {!repliesLoading && replyTree.length === 0 && (
        <p className="text-center text-dim py-10 font-display text-sm">
          No replies yet
        </p>
      )}
    </div>
  );
}
